/* eslint-disable @typescript-eslint/member-ordering */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/naming-convention */
import { Component, OnInit } from "@angular/core";
import { FetchData } from "app/shared/generalMethods";
import { MatSnackBar } from "@angular/material/snack-bar";
import { DatePipe } from "@angular/common";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

@Component({
  selector: "app-admin-reports",
  templateUrl: "./admin-reports.component.html",
  styleUrls: ["./admin-reports.component.scss"],
  providers: [DatePipe],
})
export class AdminReportsComponent implements OnInit {
  // Data objects
  lstClassSchedules: any[] = [];
  lstAttendance: any[] = [];
  lstRegistrationSchedules: any[] = [];
  lstParents: any[] = [];
  lstChildren: any[] = [];
  lstPrograms: any[] = [];
  lstAdministratorData: any[] = [];

  // Filtered data
  filteredClassSchedules: any[] = [];
  filteredAttendance: any[] = [];
  filteredPayments: any[] = [];
  filteredParentChildren: any[] = [];
  filteredRegistrations: any[] = [];

  // Filters
  dateRange = { start: null as Date | null, end: null as Date | null };
  selectedProgram: string = "";
  selectedClassSchedule: string = "";
  selectedReportType: string = "all";

  // Summaries
  attendanceSummary: any = null;
  paymentSummary: any = null;
  parentChildrenSummary: any = null;
  registrationSummary: any = null;

  // Permission flags
  canViewReports: boolean = false;
  canDownloadReports: boolean = false;
  isAdmin: boolean = false;

  constructor(
    private _fetchData: FetchData,
    private _snackBar: MatSnackBar,
    private datePipe: DatePipe
  ) {}

  ngOnInit(): void {
    this.checkPermissions();
    if (this.canViewReports) {
      this.loadData();
    }
  }

  private checkPermissions(): void {
    const { countAdmin, countStaff, lstStaffData } = this._fetchData.data;

    // Admin has all permissions
    if (countAdmin === 1) {
      this.isAdmin = true;
      this.canViewReports = true;
      this.canDownloadReports = true;
      return;
    }

    // Check staff permissions
    if (countStaff === 1 && lstStaffData && lstStaffData.length > 0) {
      const permissionSets = lstStaffData[0].Permission_Sets__c;
      if (permissionSets) {
        try {
          const permissions = JSON.parse(
            permissionSets.replace(/&quot;/g, '"')
          );
          if (permissions.reports) {
            this.canViewReports = permissions.reports.view || false;
            this.canDownloadReports = permissions.reports.download || false;
          }
        } catch (error) {
          console.error("Error parsing permissions:", error);
        }
      }
    }
  }

  // Show/hide report sections based on selected report type
  get showAttendanceReport(): boolean {
    return (
      this.canViewReports &&
      (this.selectedReportType === "all" ||
        this.selectedReportType === "attendance")
    );
  }

  get showPaymentReport(): boolean {
    return (
      this.canViewReports &&
      (this.selectedReportType === "all" ||
        this.selectedReportType === "payment")
    );
  }

  get showParentChildrenReport(): boolean {
    return (
      this.canViewReports &&
      (this.selectedReportType === "all" ||
        this.selectedReportType === "parentChildren")
    );
  }

  get showRegistrationReport(): boolean {
    return (
      this.canViewReports &&
      (this.selectedReportType === "all" ||
        this.selectedReportType === "registration")
    );
  }

  loadData(): void {
    if (!this.canViewReports) return;

    const dataSet = this._fetchData.data;
    if (!dataSet) {
      this._snackBar.open("No data available", "Close", { duration: 3000 });
      return;
    }

    this.lstClassSchedules = dataSet.lstClassSchedules || [];
    this.lstAttendance = dataSet.lstAttendance || [];
    this.lstRegistrationSchedules = dataSet.lstRegistrationSchedules || [];
    this.lstParents = dataSet.lstParents || [];
    this.lstAdministratorData = dataSet.lstAdministratorData || [];
    this.lstPrograms = this.extractAdminPrograms();
    this.lstChildren = [];

    this.lstParents.forEach((parent) => {
      const kids = parent.Kids__r?.records || [];
      this.lstChildren.push(...kids);
    });

    this.filteredClassSchedules = this.filterAdminClassSchedules();
    this.filteredParentChildren = this.filterParentChildrenData();
    this.applyFilters();
  }

  extractAdminPrograms(): any[] {
    const adminId = this.getAdminId(); // Centralized logic

    if (!adminId) {
      return [];
    }

    const programsMap = new Map<string, any>();

    this.lstClassSchedules.forEach((cls) => {
      if (cls.Program__c && cls.Program__r.Administrator__c === adminId) {
        if (!programsMap.has(cls.Program__c)) {
          programsMap.set(cls.Program__c, {
            Id: cls.Program__c,
            Name: cls.Program__r.Name,
          });
        }
      }
    });

    return Array.from(programsMap.values());
  }

  filterAdminClassSchedules(): any[] {
    const adminId = this.getAdminId(); // Reuse the same logic

    if (!adminId) {
      return [];
    }

    return this.lstClassSchedules.filter(
      (cls) => cls.Program__c && cls.Program__r.Administrator__c === adminId
    );
  }

  private getAdminId(): string | null {
    // Try from lstAdministratorData
    const idFromAdminData = this.lstAdministratorData?.[0]?.Id;

    if (idFromAdminData) {
      localStorage.setItem("adminId", idFromAdminData);
      return idFromAdminData;
    }

    // Try from lstStaffData
    const idFromStaffData =
      this._fetchData?.data?.lstStaffData?.[0]?.Administrator__c;

    if (idFromStaffData) {
      localStorage.setItem("adminId", idFromStaffData);
      return idFromStaffData;
    }

    // Try from localStorage
    return localStorage.getItem("adminId");
  }

  onProgramChange(): void {
    if (this.selectedProgram) {
      this.filteredClassSchedules = this.filterAdminClassSchedules().filter(
        (cls) => cls.Program__c === this.selectedProgram
      );
    } else {
      this.filteredClassSchedules = this.filterAdminClassSchedules();
    }
    this.selectedClassSchedule = "";
    this.applyFilters();
  }

  applyFilters(): void {
    // Filter attendance data
    this.filteredAttendance = this.filterAttendanceData();
    this.attendanceSummary = this.calculateAttendanceSummary(
      this.filteredAttendance
    );

    // Filter payment data
    this.filteredPayments = this.filterPaymentData();
    this.paymentSummary = this.calculatePaymentSummary(this.filteredPayments);

    // Filter parent & children data
    this.filteredParentChildren = this.filterParentChildrenData();
    this.parentChildrenSummary = this.calculateParentChildrenSummary(
      this.filteredParentChildren
    );

    // Filter registration data
    this.filteredRegistrations = this.filterRegistrationData();
    this.registrationSummary = this.calculateRegistrationSummary(
      this.filteredRegistrations
    );
  }

  filterAttendanceData(): any[] {
    let result = [...this.lstAttendance];

    // First filter by admin programs
    const adminClassIds = this.filterAdminClassSchedules().map((cls) => cls.Id);
    result = result.filter((item) =>
      adminClassIds.includes(item.Class_Schedule__c)
    );

    // Then apply other filters
    if (this.selectedClassSchedule) {
      result = result.filter(
        (item) => item.Class_Schedule__c === this.selectedClassSchedule
      );
    } else if (this.selectedProgram) {
      const classIds = this.filteredClassSchedules.map((cls) => cls.Id);
      result = result.filter((item) =>
        classIds.includes(item.Class_Schedule__c)
      );
    }

    // Filter by date range
    if (this.dateRange.start && this.dateRange.end) {
      result = result.filter((item) => {
        const itemDate = new Date(item.Program_Date__c || item.CreatedDate);
        return (
          itemDate >= this.dateRange.start! && itemDate <= this.dateRange.end!
        );
      });
    }

    return result;
  }

  filterPaymentData(): any[] {
    let result = [...this.lstRegistrationSchedules];

    // First filter by admin programs
    const adminClassIds = this.filterAdminClassSchedules().map((cls) => cls.Id);
    result = result.filter((item) =>
      adminClassIds.includes(item.Class_Schedule__c)
    );

    // Then apply other filters
    if (this.selectedClassSchedule) {
      result = result.filter(
        (item) => item.Class_Schedule__c === this.selectedClassSchedule
      );
    } else if (this.selectedProgram) {
      const classIds = this.filteredClassSchedules.map((cls) => cls.Id);
      result = result.filter((item) =>
        classIds.includes(item.Class_Schedule__c)
      );
    }

    // Filter by date range
    if (this.dateRange.start && this.dateRange.end) {
      result = result.filter((item) => {
        const itemDate = new Date(item.Program_Date__c || item.CreatedDate);
        return (
          itemDate >= this.dateRange.start! && itemDate <= this.dateRange.end!
        );
      });
    }

    return result;
  }

  filterParentChildrenData(): any[] {
    let result = [];

    // Get all registrations (filtered by admin first)
    let registrations = [...this.lstRegistrationSchedules];
    const adminClassIds = this.filterAdminClassSchedules().map((cls) => cls.Id);
    registrations = registrations.filter((item) =>
      adminClassIds.includes(item.Class_Schedule__c)
    );

    // Then apply other filters
    if (this.selectedClassSchedule) {
      registrations = registrations.filter(
        (item) => item.Class_Schedule__c === this.selectedClassSchedule
      );
    } else if (this.selectedProgram) {
      const classIds = this.filteredClassSchedules.map((cls) => cls.Id);
      registrations = registrations.filter((item) =>
        classIds.includes(item.Class_Schedule__c)
      );
    }

    const parentChildrenMap = new Map<string, any>();

    registrations.forEach((reg) => {
      const childId = reg.Kids__c;

      const parent = this.lstParents.find((p) =>
        p.Kids__r?.records?.some((k: any) => k.Id === childId)
      );

      if (!parent) {
        return;
      }

      const parentId = parent.Id;

      if (!parentChildrenMap.has(parentId)) {
        parentChildrenMap.set(parentId, {
          parentId,
          parentName: parent.Name,
          parentEmail: parent.Parent_Email__c,
          parentPhone: parent.Home_Phone__c,
          children: [],
        });
      }

      const parentEntry = parentChildrenMap.get(parentId);
      const childExists = parentEntry.children.some(
        (c: any) => c.childId === childId
      );

      if (!childExists) {
        const child =
          parent.Kids__r?.records?.find((c: any) => c.Id === childId) || {};
        const classInfo =
          this.lstClassSchedules.find((c) => c.Id === reg.Class_Schedule__c) ||
          {};
        const registrationStatus = child.Is_Deleted__c ? "Cancelled" : "Active";
        parentEntry.children.push({
          childId,
          childName: child.Name,
          programName: classInfo.Program__r?.Name || "N/A",
          programFee: reg.Program_Fee__c,
          paidAmount: reg.Paid_Fee__c,
          balanceDue: reg.Total_Due__c,
          paymentStatus: reg.Total_Due__c <= 0 ? "Paid" : "Pending",
          registrationStatus: registrationStatus,
        });
      }
    });
    result = Array.from(parentChildrenMap.values());

    return result;
  }

  filterRegistrationData(): any[] {
    let result = [...this.lstRegistrationSchedules];

    // First filter by admin programs
    const adminClassIds = this.filterAdminClassSchedules().map((cls) => cls.Id);
    result = result.filter((item) =>
      adminClassIds.includes(item.Class_Schedule__c)
    );

    // Then apply other filters
    if (this.selectedClassSchedule) {
      result = result.filter(
        (item) => item.Class_Schedule__c === this.selectedClassSchedule
      );
    } else if (this.selectedProgram) {
      const classIds = this.filteredClassSchedules.map((cls) => cls.Id);
      result = result.filter((item) =>
        classIds.includes(item.Class_Schedule__c)
      );
    }

    // Filter by date range
    if (this.dateRange.start && this.dateRange.end) {
      result = result.filter((item) => {
        const itemDate = new Date(item.Program_Date__c || item.CreatedDate);
        return (
          itemDate >= this.dateRange.start! && itemDate <= this.dateRange.end!
        );
      });
    }

    return result;
  }

  calculateAttendanceSummary(data: any[]): any {
    const presentCount = data.filter((a) => !a.Is_Absent__c).length;
    const absentCount = data.filter((a) => a.Is_Absent__c).length;

    return {
      total: data.length,
      present: presentCount,
      absent: absentCount,
      presentPercentage: data.length
        ? Math.round((presentCount / data.length) * 100)
        : 0,
      absentPercentage: data.length
        ? Math.round((absentCount / data.length) * 100)
        : 0,
    };
  }

  calculatePaymentSummary(data: any[]): any {
    const totalRevenue = data.reduce(
      (sum, reg) => sum + (reg.Paid_Fee__c || 0),
      0
    );
    const totalPending = data.reduce(
      (sum, reg) => sum + (reg.Total_Due__c || 0),
      0
    );

    const totalAmount = totalRevenue + totalPending;

    return {
      totalRevenue: Math.round(totalAmount),
      collected: Math.round(totalRevenue),
      pending: Math.round(totalPending),
      collectedPercentage: totalAmount
        ? Math.round((totalRevenue / totalAmount) * 100)
        : 0,
    };
  }

  calculateParentChildrenSummary(data: any[]): any {
    const totalParents = data.length;
    const totalChildren = data.reduce(
      (sum, item) => sum + item.children.length,
      0
    );
    const activeRegistrations = data.reduce(
      (sum, item) =>
        sum +
        item.children.filter((c: any) => c.registrationStatus === "Active")
          .length,
      0
    );

    return {
      totalParents,
      totalChildren,
      activeRegistrations,
    };
  }

  calculateRegistrationSummary(data: any[]): any {
    // Count active and cancelled based on Kid's Is_Deleted__c
    let activeCount = 0;
    let cancelledCount = 0;

    data.forEach((reg) => {
      const kid = this.lstChildren.find((k) => k.Id === reg.Kids__c) || {};
      if (kid.Is_Deleted__c) {
        cancelledCount++;
      } else {
        activeCount++;
      }
    });

    const total = data.length;

    return {
      total: total,
      active: activeCount,
      cancelled: cancelledCount,
      activePercentage: total ? Math.round((activeCount / total) * 100) : 0,
    };
  }

  resetFilters(): void {
    this.selectedProgram = "";
    this.selectedClassSchedule = "";
    this.selectedReportType = "all";
    // this.setDefaultDateRange();
    this.filteredClassSchedules = [...this.lstClassSchedules];
    this.applyFilters();
  }

  downloadAttendanceReport(format: string = "csv"): void {
    if (this.filteredAttendance.length === 0) {
      this._snackBar.open(
        "No attendance records found for selected criteria",
        "Close",
        { duration: 3000 }
      );
      return;
    }

    // Prepare data
    const data = this.filteredAttendance.map((att) => {
      const status = att.Is_Absent__c ? "Absent" : "Present";
      const classInfo =
        this.lstClassSchedules.find((cs) => cs.Id === att.Class_Schedule__c) ||
        {};
      const date = att.Program_Date__c || classInfo.date__c || "N/A";

      // Find the kid to check Is_Deleted__c status
      const kid = this.lstChildren.find((k) => k.Id === att.Kids__c) || {};
      const studentStatus = kid.Is_Deleted__c ? "Cancelled" : "Active";

      return {
        "Student Name": att.Kid_name__c || "N/A",
        Class: classInfo.Name || "N/A",
        Program: classInfo.Program__r?.Name || "N/A",
        Date: this.formatDate(date),
        "Start Time": classInfo.Start_Time__c || "",
        "End Time": classInfo.End_Time__c || "",
        "Attendance Status": status,
        "Student Status": studentStatus,
      };
    });

    // Add summary rows (metrics shown in "Class" column for consistency)
    data.push({
      "Student Name": "Total Records",
      Class: this.attendanceSummary.total,
      Program: "",
      Date: "",
      "Start Time": "",
      "End Time": "",
      "Attendance Status": "",
      "Student Status": "",
    });
    data.push({
      "Student Name": "Present",
      Class: this.attendanceSummary.present,
      Program: "",
      Date: "",
      "Start Time": "",
      "End Time": "",
      "Attendance Status": "",
      "Student Status": "",
    });
    data.push({
      "Student Name": "Absent",
      Class: this.attendanceSummary.absent,
      Program: "",
      Date: "",
      "Start Time": "",
      "End Time": "",
      "Attendance Status": "",
      "Student Status": "",
    });
    data.push({
      "Student Name": "Attendance Percentage",
      Class: this.attendanceSummary.presentPercentage + "%",
      Program: "",
      Date: "",
      "Start Time": "",
      "End Time": "",
      "Attendance Status": "",
      "Student Status": "",
    });

    // Also add student status summary
    const cancelledCount = data.filter(
      (row) =>
        row["Student Status"] === "Cancelled" &&
        row["Student Name"] !== "Total Records" &&
        row["Student Name"] !== "Present" &&
        row["Student Name"] !== "Absent" &&
        row["Student Name"] !== "Attendance Percentage"
    ).length;

    const activeCount = this.attendanceSummary.total - cancelledCount;

    data.push({
      "Student Name": "Active Students",
      Class: activeCount,
      Program: "",
      Date: "",
      "Start Time": "",
      "End Time": "",
      "Attendance Status": "",
      "Student Status": "",
    });
    data.push({
      "Student Name": "Cancelled Students",
      Class: cancelledCount,
      Program: "",
      Date: "",
      "Start Time": "",
      "End Time": "",
      "Attendance Status": "",
      "Student Status": "",
    });

    // Generate filename
    let filename = "Attendance_Report";
    if (this.selectedClassSchedule) {
      const classInfo = this.lstClassSchedules.find(
        (cs) => cs.Id === this.selectedClassSchedule
      );
      filename += `_${classInfo?.Name || "Class"}`;
    }

    this.exportData(data, filename, format);
  }

  downloadPaymentReport(format: string = "csv"): void {
    if (this.filteredPayments.length === 0) {
      this._snackBar.open(
        "No payment records found for selected criteria",
        "Close",
        { duration: 3000 }
      );
      return;
    }
    // Prepare data
    const data = this.filteredPayments.map((reg) => {
      const paymentStatus = reg.Total_Due__c <= 0 ? "Paid" : "Pending";

      // Find the kid to check Is_Deleted__c
      const kid = this.lstChildren.find((k) => k.Id === reg.Kids__c) || {};

      // UPDATED: Check Kid's Is_Deleted__c for registration status
      const regStatus = kid.Is_Deleted__c ? "Cancelled" : "Active";

      const classInfo =
        this.lstClassSchedules.find((cs) => cs.Id === reg.Class_Schedule__c) ||
        {};
      const date = reg.Program_Date__c || reg.CreatedDate || "N/A";

      return {
        "Student Name": reg.Kid_name__c || "N/A",
        Class: classInfo.Name || "N/A",
        Program: classInfo.Program__r?.Name || "N/A",
        Date: this.formatDate(date),
        "Program Fee": reg.Program_Fee__c || 0,
        "Paid Amount": reg.Paid_Fee__c || 0,
        "Balance Due": reg.Total_Due__c || 0,
        "Payment Status": paymentStatus,
        "Registration Status": regStatus,
      };
    });

    // Add summary data (remove debug fields from summary rows)
    data.push({
      "Student Name": "Total Registrations",
      Class: this.filteredPayments.length,
      Program: "",
      Date: "",
      "Program Fee": "",
      "Paid Amount": "",
      "Balance Due": "",
      "Payment Status": "",
      "Registration Status": "",
    });
    data.push({
      "Student Name": "Total Revenue",
      Class: this.paymentSummary.totalRevenue,
      Program: "",
      Date: "",
      "Program Fee": "",
      "Paid Amount": "",
      "Balance Due": "",
      "Payment Status": "",
      "Registration Status": "",
    });
    data.push({
      "Student Name": "Collected",
      Class: this.paymentSummary.collected,
      Program: "",
      Date: "",
      "Program Fee": "",
      "Paid Amount": "",
      "Balance Due": "",
      "Payment Status": "",
      "Registration Status": "",
    });
    data.push({
      "Student Name": "Pending",
      Class: this.paymentSummary.pending,
      Program: "",
      Date: "",
      "Program Fee": "",
      "Paid Amount": "",
      "Balance Due": "",
      "Payment Status": "",
      "Registration Status": "",
    });
    data.push({
      "Student Name": "Collection Percentage",
      Class: this.paymentSummary.collectedPercentage + "%",
      Program: "",
      Date: "",
      "Program Fee": "",
      "Paid Amount": "",
      "Balance Due": "",
      "Payment Status": "",
      "Registration Status": "",
    });

    // Generate filename
    let filename = "Payment_Report";
    if (this.selectedClassSchedule) {
      const classInfo = this.lstClassSchedules.find(
        (cs) => cs.Id === this.selectedClassSchedule
      );
      filename += `_${classInfo?.Name || "Class"}`;
    }

    this.exportData(data, filename, format);
  }

  downloadParentChildrenReport(format: string = "csv"): void {
    if (this.filteredParentChildren.length === 0) {
      this._snackBar.open(
        "No parent/children records found for selected criteria",
        "Close",
        { duration: 3000 }
      );
      return;
    }

    // Prepare data
    const data = this.filteredParentChildren.flatMap((parent) => {
      return parent.children.map((child: any) => {
        return {
          "Parent Name": parent.parentName || "N/A",
          "Parent Email": parent.parentEmail || "N/A",
          "Parent Phone": parent.parentPhone || "N/A",
          "Child Name": child.childName || "N/A",
          Program: child.programName || "N/A",
          "Registration Status": child.registrationStatus || "N/A",
        };
      });
    });

    // Add summary rows
    data.push({
      "Parent Name": "Total Parents",
      "Parent Email": "",
      "Parent Phone": "",
      "Child Name": "",
      Program: "",
      "Registration Status": this.parentChildrenSummary.totalParents,
    });
    data.push({
      "Parent Name": "Total Children",
      "Parent Email": "",
      "Parent Phone": "",
      "Child Name": "",
      Program: "",
      "Registration Status": this.parentChildrenSummary.totalChildren,
    });
    data.push({
      "Parent Name": "Active Registrations",
      "Parent Email": "",
      "Parent Phone": "",
      "Child Name": "",
      Program: "",
      "Registration Status": this.parentChildrenSummary.activeRegistrations,
    });

    // Generate filename
    let filename = "Parent_Children_Report";
    if (this.selectedClassSchedule) {
      const classInfo = this.lstClassSchedules.find(
        (cs) => cs.Id === this.selectedClassSchedule
      );
      filename += `_${classInfo?.Name || "Class"}`;
    } else if (this.selectedProgram) {
      const program = this.lstPrograms.find(
        (p) => p.Id === this.selectedProgram
      );
      filename += `_${program?.Name || "Program"}`;
    }

    this.exportData(data, filename, format);
  }

  downloadRegistrationReport(format: string = "csv"): void {
    if (this.filteredRegistrations.length === 0) {
      this._snackBar.open(
        "No registration records found for selected criteria",
        "Close",
        { duration: 3000 }
      );
      return;
    }

    // Prepare data
    const data = this.filteredRegistrations.map((reg) => {
      // Find the kid to check Is_Deleted__c status
      const kid = this.lstChildren.find((k) => k.Id === reg.Kids__c) || {};
      const regStatus = kid.Is_Deleted__c ? "Cancelled" : "Active";

      const classInfo =
        this.lstClassSchedules.find((cs) => cs.Id === reg.Class_Schedule__c) ||
        {};
      const date = reg.Program_Date__c || reg.CreatedDate || "N/A";

      // Determine fee status with proper null handling
      const paidFee = reg.Paid_Fee__c || 0;
      const programFee = reg.Program_Fee__c || 0;
      const feeStatus = paidFee >= programFee ? "Paid" : "Unpaid";
      return {
        "Student Name": reg.Kid_name__c || "N/A",
        "Parent Email": reg.Parent_Email__c || "N/A",
        Class: classInfo.Name || "N/A",
        Program: classInfo.Program__r?.Name || "N/A",
        "Registration Date": this.formatDate(date),
        "Program Fee": programFee,
        "Fee Status": feeStatus,
        Status: regStatus,
      };
    });

    // Add summary rows (following the same pattern as other reports)
    data.push({
      "Student Name": "Total Registrations",
      "Parent Email": "",
      Class: "",
      Program: "",
      "Registration Date": "",
      "Program Fee": this.registrationSummary.total,
      "Fee Status": "",
      Status: "",
    });
    data.push({
      "Student Name": "Active",
      "Parent Email": "",
      Class: "",
      Program: "",
      "Registration Date": "",
      "Program Fee": this.registrationSummary.active,
      "Fee Status": "",
      Status: "",
    });
    data.push({
      "Student Name": "Cancelled",
      "Parent Email": "",
      Class: "",
      Program: "",
      "Registration Date": "",
      "Program Fee": this.registrationSummary.cancelled,
      "Fee Status": "",
      Status: "",
    });
    data.push({
      "Student Name": "Active Percentage",
      "Parent Email": "",
      Class: "",
      Program: "",
      "Registration Date": "",
      "Program Fee": this.registrationSummary.activePercentage + "%",
      "Fee Status": "",
      Status: "",
    });

    // Generate filename
    let filename = "Registration_Report";
    if (this.selectedClassSchedule) {
      const classInfo = this.lstClassSchedules.find(
        (cs) => cs.Id === this.selectedClassSchedule
      );
      filename += `_${classInfo?.Name || "Class"}`;
    } else if (this.selectedProgram) {
      const program = this.lstPrograms.find(
        (p) => p.Id === this.selectedProgram
      );
      filename += `_${program?.Name || "Program"}`;
    }

    this.exportData(data, filename, format);
  }

  private exportData(
    data: any[],
    filename: string,
    format: string = "csv"
  ): void {
    if (format === "xlsx") {
      // Excel export
      const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(data);
      const wb: XLSX.WorkBook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Report");
      XLSX.writeFile(wb, `${filename}.xlsx`);
    } else if (format === "pdf") {
      // PDF export
      this.exportToPDF(data, filename);
    } else {
      // CSV export
      const csvContent = this.convertToCSV(data);
      this.downloadFile(csvContent, `${filename}.csv`, "text/csv");
    }
  }
  private exportToPDF(data: any[], filename: string): void {
    // Separate summary rows from data rows
    const summaryRows = data.filter(
      (row) =>
        row["Student Name"] &&
        [
          "Total Registrations",
          "Active",
          "Cancelled",
          "Active Percentage",
          "Total Parents",
          "Total Children",
          "Active Registrations",
          "Total Records",
          "Present",
          "Absent",
          "Attendance Percentage",
          "Total Revenue",
          "Collected",
          "Pending",
          "Collection Percentage",
          "Paid Registrations",
          "Unpaid Registrations",
        ] // Added new summary types
          .includes(row["Student Name"])
    );

    // Filter out summary rows from data for the main table
    const dataRows = data.filter(
      (row) =>
        !summaryRows.includes(row) &&
        !Object.values(row).every(
          (value) => value === undefined || value === ""
        )
    );

    // Create a new PDF document
    const doc = new jsPDF("p", "pt");

    // Title
    const title = filename.replace(/_/g, " ");
    doc.setFontSize(18);
    doc.text(title, 40, 40);

    // Date
    doc.setFontSize(10);
    doc.text(`Generated on: ${this.formatDate(new Date().toString())}`, 40, 60);

    // Filter info if available
    let y = 80;
    if (
      this.selectedProgram ||
      this.selectedClassSchedule ||
      (this.dateRange.start && this.dateRange.end)
    ) {
      if (this.selectedProgram) {
        const program = this.lstPrograms.find(
          (p) => p.Id === this.selectedProgram
        );
        doc.text(`Program: ${program?.Name || this.selectedProgram}`, 40, y);
        y += 20;
      }

      if (this.selectedClassSchedule) {
        const classInfo = this.lstClassSchedules.find(
          (cs) => cs.Id === this.selectedClassSchedule
        );
        doc.text(
          `Class: ${classInfo?.Name || this.selectedClassSchedule}`,
          40,
          y
        );
        y += 20;
      }

      if (this.dateRange.start && this.dateRange.end) {
        doc.text(
          `Date Range: ${this.formatDate(
            this.dateRange.start.toString()
          )} to ${this.formatDate(this.dateRange.end.toString())}`,
          40,
          y
        );
        y += 20;
      }
    }

    // Prepare summary table data if exists
    let summaryY = y;

    if (summaryRows.length > 0) {
      // Get summary headers (metric and value columns)
      const summaryHeaders = ["Metric", "Value"];
      const summaryData = summaryRows.map((row) => {
        const metric = row["Student Name"] || row["Parent Name"] || "";
        const value = Object.values(row).find(
          (val) =>
            val !== undefined && val !== "" && val !== metric && val !== null
        );
        return [metric, value ? value.toString() : ""];
      });

      // Add summary table
      autoTable(doc, {
        head: [summaryHeaders],
        body: summaryData,
        startY: summaryY,
        theme: "grid",
        headStyles: {
          fillColor: [41, 128, 185],
          textColor: 255,
          fontStyle: "bold",
        },
        styles: {
          fontSize: 10,
          cellPadding: 6,
        },
        columnStyles: {
          0: { fontStyle: "bold" },
        },
      });

      // Update Y position for main table
      summaryY = (doc as any).lastAutoTable.finalY + 20;
    }

    // Prepare data for the main table
    if (dataRows.length > 0) {
      const headers = Object.keys(dataRows[0]);
      const tableData = dataRows.map((row) => {
        return headers.map((header) => {
          const value = row[header];

          // Handle different data types for PDF display
          if (value === undefined || value === null) {
            return "";
          }

          // Format numbers with 2 decimal places for currency values
          if (
            header.toLowerCase().includes("fee") ||
            header.toLowerCase().includes("amount") ||
            header.toLowerCase().includes("due")
          ) {
            return typeof value === "number"
              ? value.toFixed(2)
              : value.toString();
          }

          // For status fields, ensure they're strings
          if (header === "Fee Status" || header === "Status") {
            return value.toString();
          }

          return value.toString();
        });
      });

      // Add the main table
      autoTable(doc, {
        head: [headers],
        body: tableData,
        startY: summaryY,
        styles: {
          fontSize: 8,
          cellPadding: 3,
          overflow: "linebreak",
        },
        headStyles: {
          fillColor: [41, 128, 185],
          textColor: 255,
          fontStyle: "bold",
        },
        alternateRowStyles: {
          fillColor: [245, 245, 245],
        },
        didDrawPage: (data) => {
          // Footer
          doc.setFontSize(10);
          doc.setTextColor(150);
          doc.text(
            `Page ${data.pageNumber}`,
            doc.internal.pageSize.width / 2,
            doc.internal.pageSize.height - 20,
            { align: "center" }
          );
        },
      });
    }

    // Save the PDF
    doc.save(`${filename}.pdf`);
  }

  private convertToCSV(data: any[]): string {
    if (data.length === 0) {
      return "";
    }

    // Extract headers
    const headers = Object.keys(data[0]);
    let csv = headers.join(",") + "\n";

    // Add rows
    data.forEach((row) => {
      const values = headers.map((header) => {
        // Escape quotes in values
        const value = row[header] !== undefined ? row[header] : "";
        return typeof value === "string"
          ? `"${value.replace(/"/g, '""')}"`
          : value;
      });
      csv += values.join(",") + "\n";
    });

    return csv;
  }

  private downloadFile(content: string, filename: string, type: string): void {
    const blob = new Blob([content], { type: `${type};charset=utf-8;` });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);

    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = "hidden";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  private formatDate(dateString: string): string {
    if (!dateString) {
      return "N/A";
    }
    return this.datePipe.transform(dateString, "mediumDate") || "N/A";
  }

  private formatDateForFilename(date: Date): string {
    return date.toISOString().slice(0, 10).replace(/-/g, "");
  }
}
